/* eslint-disable */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Checkbox, FormControlLabel, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';
import './permission.scss';
import SearchDropdown from '../../Components/Dropdown/dropdown';
import ButtonComponent from '../../Components/Button/button';
import { SAVE, SELECT } from '../../Constants/strings';
import { updateRole, getAllRoles, getUserRoles } from '../../Api/request/userRoles';
import { getEmployeeMasterList, getDepartmentsList } from '../../Api/request/projectDetails';
import UseAxios from '../../Api/http/http';
import { disableTask, tasks, pageConfig } from './config';
import SimpleSnackbar from '../../Components/Snackbar/snackbar';
import PermissionSubModule from './permissionSubModule';
import { removeDefaultStyles } from '../../Utils/buttonStyle';
import { usePermissionsData } from './configApi';
import ArrowDropDown from '../../Assets/images/svg/drop-down-arrow.svg';
import expandIcon from '../../Assets/images/svg/dar_collection_dashboard_expand_all.svg';
import collapseIcon from '../../Assets/images/svg/dar_collection_dashboard_collapse_all.svg';
import EmployeePermissionFilters from './EmployeePermissionFilters';
import listViewIcon from '../../Assets/images/svg/user_circle_check.svg';
import rejectPurpleIcon from '../../Assets/images/svg/Vector.svg';
import downloadIcon from '../../Assets/images/svg/dar_collection_dashboard_download.svg';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import excelIcon from '../../Assets/images/svg/excel.svg';
import excelhoverIcon from '../../Assets/images/svg/excel_hover.svg';
import { getPermissionDataExcel } from '../../Api/request/reports';
import Loader from '../../Components/Loader';
import { MODULE_NAMES } from '../../Constants/modules';

const normalizeEmployees = (rows = [], departmentMap = {}) => rows.map((employee) => {
  const deptId = employee.departmentId ?? employee.department_id;
  const deptName = employee.department
    || employee.departmentName
    || employee.departmentDetails?.dept_name
    || employee.departmentDetails?.department_name
    || (deptId && departmentMap[deptId])
    || '-';

  const activeVal = employee.isActive ?? employee.is_active;
  let status = 'Active';
  if (activeVal === 'Y' || activeVal === 1 || activeVal === true || activeVal === 'Active') {
    status = 'Active';
  } else if (activeVal === 'N' || activeVal === 0 || activeVal === false || activeVal === 'Inactive') {
    status = 'Inactive';
  } else if (activeVal) {
    status = String(activeVal);
  }

  return {
    id: String(employee.empNo ?? employee.empId),
    employeeId: String(employee.empId ?? ''),
    name: [employee.firstName, employee.lastName].filter(Boolean).join(' ')
      .trim(),
    email: employee.emailId || employee.email || '',
    vertical: employee.vertical || employee.employeeVerticalName || '-',
    designation: employee.designation || '-',
    department: deptName || '-',
    departmentId: deptId ? String(deptId) : '',
    status: status || '-',
  };
}).sort((left, right) => left.name.localeCompare(right.name));

const permissionMap = (permissions = []) => permissions.reduce((result, permission) => {
  if (!permission?.module) return result;
  result[permission.module] = [...new Set([
    ...(result[permission.module] || []), ...(permission.actions || []),
  ])];
  return result;
}, {});

const enforceBudgetChangeRequestRules = (configTree) => {
  const syncNodeActions = (node) => {
    if (!node || typeof node !== 'object') return node;

    let effortsNode = null;
    let costNode = null;

    Object.values(node).forEach((value) => {
      if (!value || typeof value !== 'object') return;

      if (value.module === MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_EFFORTS) {
        effortsNode = value;
      }

      if (value.module === MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_COST) {
        costNode = value;
      }

      if (value.content) {
        syncNodeActions(value.content);
      }

      if (value.subMenu) {
        Object.values(value.subMenu).forEach((subMenuValue) => {
          if (subMenuValue?.content) {
            syncNodeActions(subMenuValue.content);
          }
        });
      }
    });

    if (costNode?.actions?.Add && effortsNode?.actions) {
      effortsNode.actions = {
        ...effortsNode.actions,
        Add: true,
      };
    }

    return node;
  };

  return syncNodeActions(configTree);
};

const getModuleCandidates = (moduleName) => {
  if (!moduleName) return [];

  return Array.from(new Set([
    moduleName,
    moduleName.replace(/-/g, '_'),
    moduleName.replace(/_/g, '-'),
  ]));
};

const normalizeConfigNode = (node) => {
  if (!node || typeof node !== 'object') return node;

  const normalizedNode = { ...node };
  const content = normalizeConfigTree(normalizedNode.content || {});
  const subMenuContent = Object.entries(normalizedNode.subMenu || {}).reduce(
    (acc, [subMenuKey, subMenuValue]) => {
      const subMenuTitle = subMenuValue?.title || subMenuKey;
      acc[subMenuTitle] = normalizeConfigNode({
        ...subMenuValue,
        active: subMenuValue?.active ?? subMenuValue?.isActive ?? false,
      });
      return acc;
    },
    {},
  );

  delete normalizedNode.subMenu;
  delete normalizedNode.isActive;

  if (
    Object.keys(content).length ||
    Object.keys(subMenuContent).length ||
    normalizedNode.content
  ) {
    normalizedNode.content = {
      ...content,
      ...subMenuContent,
    };
  }

  return normalizedNode;
};

const normalizeConfigTree = (tree = {}) => Object.entries(tree || {}).reduce(
  (acc, [key, value]) => {
    acc[key] = normalizeConfigNode(value);
    return acc;
  },
  {},
);

const Permission = () => {
  const permissionLabel = 'Permissions';
  const [userRoles, setUserRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [roleValue, setRoleValue] = useState('');
  const [employees, setEmployees] = useState([]);
  const [employeeValue, setEmployeeValue] = useState(null);
  const [employeeLookupVersion, setEmployeeLookupVersion] = useState(0);
  const [employeeRoles, setEmployeeRoles] = useState([]);
  const [employeeError, setEmployeeError] = useState('');
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [directoryError, setDirectoryError] = useState('');
  const selectionRequest = useRef(0);
  const [userDetails, setUserDetails] = useState([]);
  const [config, setConfig] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [checkboxState, setCheckboxState] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [isExcelHovered, setIsExcelHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const [viewOnlyAccess, setViewOnlyAccess] = useState(false);
  const [viewRestrictedAccess, setViewRestrictedAccess] = useState(false);
  const [roleModalData, setRoleModalData] = useState(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [actionFilters, setActionFilters] = useState({
    View: true,
    Add: true,
    Edit: true,
    Delete: true,
  });

  // Helper to collect all modules under a node recursively
  const getModulesInNode = (node) => {
    let modules = [];
    if (!node || typeof node !== 'object') return modules;
    if (node.module) {
      modules.push(node.module);
    }
    if (node.content) {
      Object.values(node.content).forEach((child) => {
        modules = modules.concat(getModulesInNode(child));
      });
    }
    if (node.subMenu) {
      Object.values(node.subMenu).forEach((child) => {
        modules = modules.concat(getModulesInNode(child));
      });
    }
    return Array.from(new Set(modules));
  };

  // Helper to compute role access list & granted actions for a node
  const getRoleAccessForNode = (node) => {
    const modulesInNode = getModulesInNode(node);
    if (!modulesInNode.length || !allRoles.length) return [];

    const result = [];
    (employeeValue ? allRoles : userRoles).filter((role) => !employeeValue || employeeRoles.some(
      (assigned) => String(assigned.role_id) === String(role.id)
    )).forEach((role) => {
      const rolePerms = role.permissions || [];
      const matchedActionsSet = new Set();

      modulesInNode.forEach((modName) => {
        const candidates = getModuleCandidates(modName);
        const perm = rolePerms.find((item) => candidates.includes(item.module));
        if (perm && perm.actions && perm.actions.length > 0) {
          perm.actions.forEach((act) => matchedActionsSet.add(act));
        }
      });

      if (matchedActionsSet.size > 0) {
        result.push({
          roleId: role.id,
          roleName: role.name,
          actions: Array.from(matchedActionsSet),
        });
      }
    });

    return result;
  };

  const isActionGranted = (node, task) => {
    if (!node) return false;
    const checked = node.actions?.[task];
    const isDisabled = disableTask(node.module, task);
    if (isDisabled && !employeeValue) return false;

    if (checked === true) return true;
    if (checked === false) return false;

    if (!node.module) return false;
    const moduleCandidates = getModuleCandidates(node.module);
    const permission = userDetails?.find((item) =>
      moduleCandidates.includes(item.module)
    );
    const backendAction = tasks[task] || task;
    return permission?.actions?.includes(backendAction) || permission?.actions?.includes(task) || false;
  };

  const hasMatchingAction = (node, granted) => {
    if (!node) return false;
    // Parent rows render their children, not their own actions.
    if (node.content) {
      return Object.values(node.content).some((child) => hasMatchingAction(child, granted));
    }
    return Object.keys(node.actions || {}).some(
      (task) => !disableTask(node.module, task) && isActionGranted(node, task) === granted
    );
  };

  const hasAccess = (node) => hasMatchingAction(node, true);
  const hasRestricted = (node) => hasMatchingAction(node, false);

  const handleToggleExpandAll = () => {
    const nextState = !isAllExpanded;
    setIsAllExpanded(nextState);
    setConfig((prevConfig) => {
      const setAllActiveRecursive = (node) => {
        if (!node || typeof node !== 'object') return node;
        const updated = {};
        Object.entries(node).forEach(([key, val]) => {
          if (!val || typeof val !== 'object') {
            updated[key] = val;
            return;
          }
          const itemCopy = { ...val };
          if (itemCopy.content || itemCopy.subMenu) {
            itemCopy.active = nextState;
            itemCopy.isActive = nextState;
          }
          if (itemCopy.content) {
            itemCopy.content = setAllActiveRecursive(itemCopy.content);
          }
          if (itemCopy.subMenu) {
            itemCopy.subMenu = setAllActiveRecursive(itemCopy.subMenu);
          }
          updated[key] = itemCopy;
        });
        return updated;
      };
      return setAllActiveRecursive(prevConfig);
    });
  };

  const employeePermissionMap = useMemo(
    () => employeeValue ? permissionMap(userDetails) : undefined,
    [employeeValue, userDetails],
  );
  const { permissions, pageAccess } = usePermissionsData(undefined, employeePermissionMap);

  const mergeConfigTrees = (baseNode = {}, extraNode = {}) => {
    const merged = { ...baseNode };

    Object.entries(extraNode || {}).forEach(([key, value]) => {
      if (!merged[key]) {
        merged[key] = value;
        return;
      }

      const baseValue = merged[key];
      const nextValue = { ...baseValue, ...value };

      if (baseValue?.content || value?.content) {
        nextValue.content = mergeConfigTrees(baseValue?.content || {}, value?.content || {});
      }

      if (baseValue?.subMenu || value?.subMenu) {
        nextValue.subMenu = mergeConfigTrees(baseValue?.subMenu || {}, value?.subMenu || {});
      }

      if (baseValue?.actions) {
        nextValue.actions = { ...baseValue.actions };
      }

      if (value?.actions) {
        nextValue.actions = { ...baseValue?.actions, ...value.actions };
      }

      merged[key] = nextValue;
    });

    return merged;
  };

  const getPermissionSkeleton = () => {
    const rawSkeleton = mergeConfigTrees(
      JSON.parse(JSON.stringify(normalizeConfigTree(pageConfig || {}))),
      JSON.parse(JSON.stringify(pageAccess || {})),
    );

    const desiredOrder = [
      'Home',
      'Dashboard',
      'Masters',
      'Users',
      'Skills',
      'Certifications',
      'Configurations',
      'GeneralConfig',
      'DealTransition',
      'StaffAugmentation',
      'OrganizationContribution',
      'Finance',
      'Delivery',
      'Projects',
      'Resource',
      'MIS',
      'EmployeeProfile'
    ];

    const sortedSkeleton = {};
    desiredOrder.forEach((key) => {
      if (rawSkeleton[key] !== undefined) {
        sortedSkeleton[key] = rawSkeleton[key];
      }
    });

    Object.keys(rawSkeleton).forEach((key) => {
      if (sortedSkeleton[key] === undefined) {
        sortedSkeleton[key] = rawSkeleton[key];
      }
    });

    return sortedSkeleton;
  };

  useEffect(() => {
    if (permissions) {
      if (employeeValue) updateInitialDetails(userDetails, true);
      else setConfig(getPermissionSkeleton());
    }
  }, [permissions])

  useEffect(() => {
    getAllUserRoles();
    let active = true;
    Promise.allSettled([
      UseAxios(getEmployeeMasterList()),
      UseAxios(getDepartmentsList()),
    ]).then(([empRes, deptRes]) => {
      if (empRes.status !== 'fulfilled' || !Array.isArray(empRes.value?.data?.employeeList)) {
        throw new Error('Invalid employee directory');
      }
      const deptData = deptRes.status === 'fulfilled'
        ? (deptRes.value?.data?.departments || deptRes.value?.data || [])
        : [];
      const deptMap = Array.isArray(deptData)
        ? deptData.reduce((acc, d) => {
            const id = d?.dept_id ?? d?.id ?? d?.department_id;
            const name = d?.dept_name ?? d?.name ?? d?.department_name;
            if (id && name) acc[id] = name;
            return acc;
          }, {})
        : {};

      if (active) setEmployees(normalizeEmployees(empRes.value.data.employeeList, deptMap));
    }).catch(() => {
      if (active) setDirectoryError('Unable to load employee filters. Please reload the page to retry.');
    }).finally(() => {
      if (active) setEmployeesLoading(false);
    });
    return () => { active = false; selectionRequest.current += 1; };
  }, []);




  const updateInitialDetails = (permissionsOverride = null, readOnly = false) => {
    const effectivePermissions = permissionsOverride || userDetails;

    const newConfig = getPermissionSkeleton(); // reset skeleton

    const applyPermissions = (node) => {
      if (!node) return;

      Object.entries(node).forEach(([key, val]) => {
        if (val.module && val.actions) {
          // find backend permission for this module
          const moduleCandidates = getModuleCandidates(val.module);
          const permission = effectivePermissions.find(
            (item) => moduleCandidates.includes(item.module)
          );

          if (permission) {
            // map backend actions into task keys
            val.actions = Object.keys(val.actions).reduce((acc, taskKey) => {
              const backendAction = tasks[taskKey]; // map key to backend action
              acc[taskKey] = permission.actions.includes(backendAction);
              return acc;
            }, {});
          } else {
            // reset all actions to false
            val.actions = Object.keys(val.actions).reduce((acc, taskKey) => {
              acc[taskKey] = false;
              return acc;
            }, {});
          }
        }

        if (val.content) applyPermissions(val.content);
        if (val.subMenu) applyPermissions(val.subMenu);
      });
    };

    applyPermissions(newConfig);
    setConfig(readOnly ? newConfig : enforceBudgetChangeRequestRules(newConfig));
  };




  const getAllUserRoles = async () => {
    let roleListNames = [];
    const token = localStorage.getItem('userRolesToken');
    UseAxios(getAllRoles(token))
      .then((response) => {
        setAllRoles((response?.data?.data?.list || []).map((role) => ({
          id: role.id,
          name: role.role,
          permissions: role?.features?.[0]?.permissions || [],
        })));
        response?.data?.data?.list.map((role) => {
          if (!role.superAdmin) {
            roleListNames.push({
              id: role.id,
              name: role.role,
              permissions: role?.features[0]['permissions'],
            });
          }
        });

        roleListNames.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        setUserRoles(roleListNames);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleSaveOnClick = async () => {
    if (employeeValue || !roleValue || isLoading) return;
    setIsLoading(true);
    const permission = [];

    // Recursive function to collect permissions from all levels
    const collectPermissions = (node) => {
      if (!node) return;
      Object.values(node).forEach((item) => {
        if (item.module && item.actions) {
          const newActions = Object.keys(item.actions || {})
            .filter((task) => item.actions[task] === true)
            .map((task) => tasks[task])
            .filter(Boolean);
          if (newActions.length) {
            permission.push({
              module: item.module,
              actions: newActions,
            });
          }
        }
        if (item.content) {
          collectPermissions(item.content);
        }
      });
    };

    collectPermissions(config);

    try {
      await modifyRole(roleValue.id, permission); // 👈 wait for API call
    } catch (err) {
      console.error("Error saving role:", err);
    } finally {
      setIsLoading(false); // 👈 only reset after API finishes
    }
  };


  const modifyRole = async (id, data) => {
    if (data.length) {
      const token = localStorage.getItem('userRolesToken');
      await UseAxios(
        updateRole(
          id,
          {
            name: roleValue.name,
            superAdmin: false,
            permissions: [...data],
          },
          token
        )
      )
        .then(async (response) => {
          setSnackbarMessage('Role updated successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          await getAllUserRoles();
          setTimeout(() => setSnackbarOpen(false), 2000);
        })
        .catch((error) => {
          setSnackbarMessage('Oops something went wrong!!');
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
          setTimeout(() => setSnackbarOpen(false), 2000);
          console.error(error);
        });
    } else {
      setSnackbarMessage('Please set required permission');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setTimeout(() => setSnackbarOpen(false), 2000);
    }
  };



  // Updated: Always fetch latest permissions from API on role change
  const handleRoleChange = async (value) => {
    const requestId = ++selectionRequest.current;
    setEmployeeValue(null);
    setEmployeeRoles([]);
    setEmployeeError('');
    setRoleModalOpen(false);
    setViewOnlyAccess(false);
    setViewRestrictedAccess(false);
    setIsAllExpanded(false);
    if (!value?.id) {
      setIsLoading(false);
      setRoleValue('');
      setUserDetails([]);
      setViewOnlyAccess(false);
      setViewRestrictedAccess(false);
      setConfig(getPermissionSkeleton());
      return;
    }

    setIsLoading(true);
    setRoleValue(value);
    setUserDetails([]);
    const token = localStorage.getItem('userRolesToken');

    try {
      const response = await UseAxios(getAllRoles(token));
      if (requestId !== selectionRequest.current) return;
      const roles = response?.data?.data?.list || response?.data?.data || [];
      const selectedRole = roles.find((role) => role.id === value.id);

      // normalize permissions
      const permissions =
        selectedRole?.features?.[0]?.permissions ||
        selectedRole?.permissions ||
        [];

      setUserDetails(permissions);

      const baseConfig = getPermissionSkeleton();
      setConfig(baseConfig);
      updateInitialDetails(permissions);
    } catch (err) {
      if (requestId !== selectionRequest.current) return;
      console.error('Error fetching latest permissions for role', err);

      // fallback if API fails: use whatever permissions came with dropdown value
      const permissions = value.permissions || [];
      setUserDetails(permissions);

      const baseConfig = getPermissionSkeleton();
      setConfig(baseConfig);
      updateInitialDetails(permissions);
    } finally {
      if (requestId === selectionRequest.current) setIsLoading(false);
    }
  };

  const handleEmployeeChange = (employee) => {
    ++selectionRequest.current;
    setEmployeeValue(employee);
    setRoleValue('');
    setEmployeeRoles([]);
    setUserDetails([]);
    setEmployeeError('');
    setRoleModalOpen(false);
    setViewOnlyAccess(false);
    setViewRestrictedAccess(false);
    setIsAllExpanded(false);
    setIsLoading(Boolean(employee));
    setEmployeeLookupVersion((version) => version + 1);
  };

  useEffect(() => {
    if (!employeeValue) return;
    const requestId = ++selectionRequest.current;
    let active = true;
    const isCurrent = () => active && requestId === selectionRequest.current;
    const loadEmployeePermissions = async () => {
      setIsLoading(true);
      setEmployeeError('');
      try {
        const email = employeeValue.email?.trim();
        if (!email) throw new Error('Employee has no mapped email');
        const response = await UseAxios(getUserRoles(localStorage.getItem('userRolesToken'), email));
        if (!isCurrent()) return;
        const users = response?.data?.data;
        if (response?.data?.success === false || !Array.isArray(users)) {
          throw new Error('Invalid employee permissions response');
        }
        const user = users.find((item) => item.emailId?.trim().toLowerCase() === email.toLowerCase());
        if (users.length && !user) throw new Error('Employee permissions response did not match the selected employee');
        if (user && (!Array.isArray(user.roleDetails) || !Array.isArray(user.permissions))) {
          throw new Error('Invalid employee roles or permissions');
        }
        setEmployeeRoles(user?.roleDetails || []);
        setUserDetails(user?.permissions || []);
      } catch (error) {
        if (isCurrent()) {
          setEmployeeError('Unable to load permissions for this employee. Please select the employee again to retry.');
        }
      } finally {
        if (isCurrent()) setIsLoading(false);
      }
    };
    loadEmployeePermissions();
    return () => { active = false; };
  }, [employeeValue, employeeLookupVersion]);

  const handleToggle = (keyPath) => {
    setConfig((prevConfig) => {
      const toggleRecursive = (obj, path) => {
        if (!obj || path.length === 0) return obj;
        const [currentKey, ...rest] = path;
        if (rest.length === 0) {
          return {
            ...obj,
            [currentKey]: {
              ...obj[currentKey],
              active: !obj[currentKey]?.active,
            },
          };
        }
        return {
          ...obj,
          [currentKey]: {
            ...obj[currentKey],
            content: toggleRecursive(obj[currentKey]?.content, rest),
          },
        };
      };
      return toggleRecursive(prevConfig, keyPath);
    });
  };

  function renderPermissionContent(content, parentKeys = [], level = 0) {
    return Object.keys(content || {})
      .filter((subItem) => {
        if (viewOnlyAccess) return hasAccess(content[subItem]);
        if (viewRestrictedAccess) return hasRestricted(content[subItem]);
        return true;
      })
      .map((subItem, subKey) => {
        const subValue = content[subItem];
        const roleAccessList = getRoleAccessForNode(subValue);
        const roleCount = roleAccessList.length;

        const handleRoleBadgeClick = (e) => {
          e.stopPropagation();
          e.preventDefault();
          setActionFilters({ View: true, Add: true, Edit: true, Delete: true });
          setRoleModalData({
            title: subItem,
            roles: roleAccessList,
          });
          setRoleModalOpen(true);
        };

        if (subValue.content) {
          // Use different class for nested levels
          const containerClass = level === 0 ? 'sub-container' : 'sub-sub-container';
          const tabClass = level === 0 ? 'tab' : 'tab sub';
          return (
            <div className={containerClass} key={parentKeys.concat(subItem).join('-')}>
              <button
                style={removeDefaultStyles}
                onClick={() => handleToggle([...parentKeys, subItem])}
              >
                <div className={tabClass}>
                  <span className="tab-icon">
                    {subValue.active ? <FaChevronDown /> : <FaChevronRight />}
                  </span>
                  <span>{subItem}</span>
                </div>
              </button>
              {Boolean(subValue.active) &&
                renderPermissionContent(subValue.content, [...parentKeys, subItem], level + 1)}
            </div>
          );
        } else {
          // Render leaf actions
          return (
            <div
              className="tab sub permission-leaf-row"
              key={parentKeys.concat(subItem).join('-')}
            >
              <span>{subItem}</span>
              {Object.keys(subValue.actions || {})
                .filter((task) => {
                  const operation = isActionGranted(subValue, task);
                  if (viewRestrictedAccess) return !operation;
                  if (viewOnlyAccess) return operation;
                  return true;
                })
                .map((task) => {
                const checked = subValue.actions[task];
                const moduleCandidates = getModuleCandidates(subValue.module);
                const permission = userDetails?.find(
                  (item) => moduleCandidates.includes(item.module)
                );
                const isDisabled = disableTask(subValue?.module, task);
                const operation = isActionGranted(subValue, task);
                return (
                  <FormControlLabel
                    key={`${subItem}-${task}`}
                    control={
                      <Checkbox
                        name={`${subItem}-${task}`}
                        checked={operation}
                        onChange={(e) =>
                          handleCheckBox(content, [...parentKeys, subItem], task, e)
                        }
                        disabled={!roleValue || disableTask(subValue?.module, task)}
                      />
                    }
                    label={task}
                  />
                );
                })}
              <Tooltip title="Role access list" arrow>
                <div
                  className="role-count-badge leaf-badge"
                  onClick={handleRoleBadgeClick}
                >
                  <img src={listViewIcon} alt="Role access list" className="badge-icon" />
                  <span>{roleCount}</span>
                </div>
              </Tooltip>
            </div>
          );
        }
      });
  }

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const handleDownloadClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCheckBox = (content, keyPath, task, e) => {
    if (employeeValue || isLoading) return;
    setConfig((prevConfig) => {
      const updateActions = (obj, path) => {
        if (!obj || path.length === 0) return obj;
        const [currentKey, ...rest] = path;
        if (rest.length === 0) {
          return {
            ...obj,
            [currentKey]: {
              ...obj[currentKey],
              actions: {
                ...obj[currentKey].actions,
                [task]: e.target.checked,
              },
            },
          };
        }
        return {
          ...obj,
          [currentKey]: {
            ...obj[currentKey],
            content: updateActions(obj[currentKey]?.content, rest),
          },
        };
      };
      return enforceBudgetChangeRequestRules(updateActions(prevConfig, keyPath));
    });
  };

  const handleToggleSubMenu = (mainKey, subKey) => {
    setConfig((prevConfig) => ({
      ...prevConfig,
      [mainKey]: {
        ...prevConfig[mainKey],
        subMenu: {
          ...prevConfig[mainKey].subMenu,
          [subKey]: {
            ...prevConfig[mainKey].subMenu[subKey],
            isActive: !prevConfig[mainKey].subMenu[subKey]?.isActive,
          },
        },
      },
    }));
  };

  const handleCheckBoxSubMenu = (
    mainKey,
    content,
    subMenu,
    subItemKey,
    subKey,
    subItem,
    task,
    e
  ) => {
    const { checked } = e.target;
    const module = content[subItemKey]?.module;
    setConfig((prevConfig) => {
      return enforceBudgetChangeRequestRules({
        ...prevConfig,
        [mainKey]: {
          ...prevConfig[mainKey],
          subMenu: {
            ...prevConfig[mainKey].subMenu,
            [subItem]: {
              ...subMenu[subItem],
              content: {
                ...subMenu[subItem]?.content,
                [subItemKey]: {
                  actions: {
                    ...content?.[subItemKey]?.actions,
                    [task]: checked,
                  },
                  module: module,
                },
              },
            },
          },
        },
      });
    });
  };

  const handleDownloadExcel = async () => {
    if (isLoading || employeeError) return;
    try {
      if (employeeValue) {
        await getPermissionDataExcel({
          email: employeeValue.email,
          employeeId: employeeValue.employeeId,
          name: employeeValue.name,
          vertical: employeeValue.vertical,
          designation: employeeValue.designation,
          department: employeeValue.department,
          status: employeeValue.status,
        });
      } else if (roleValue?.id || roleValue?.name || (typeof roleValue === 'string' && roleValue)) {
        await getPermissionDataExcel({
          roleId: roleValue?.id,
          roleName: roleValue?.name || (typeof roleValue === 'string' ? roleValue : ''),
        });
      } else {
        await getPermissionDataExcel();
      }
    } catch (error) {
      setSnackbarMessage('Oops something went wrong!!');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setTimeout(() => setSnackbarOpen(false), 4000);
    } finally {
      handleCloseMenu();
    }
  };

  return (
    <div className="permission-container">
      <h1>{permissionLabel}</h1>
      <div className="skill-search-and-buttons">
        <div className="skill-search">
          <SearchDropdown
            options={userRoles}
            dropdownCss={'skill-search-dropdown'}
            searchBox={true}
            disablePortal={true}
            label={'Role'}
            placeholder={SELECT}
            value={roleValue || null}
            onChange={(event) => {
              handleRoleChange(event.target.value);
            }}
            id="skill-department-dropdown"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {!employeesLoading && !directoryError && (
            <EmployeePermissionFilters employees={employees} employee={employeeValue} onChange={handleEmployeeChange} />
          )}
          <Tooltip title={viewOnlyAccess ? 'Show All Permissions' : 'View Permissions'} arrow>
            <button
              onClick={() => {
                setViewOnlyAccess((prev) => !prev);
                setViewRestrictedAccess(false);
              }}
              className={`expand-permission-icon-btn ${viewOnlyAccess ? 'active-icon-btn' : ''} ${!roleValue && !employeeValue ? 'disabled-btn' : ''}`}
              aria-pressed={viewOnlyAccess}
              disabled={(!roleValue && !employeeValue) || isLoading || Boolean(employeeError)}
              type="button"
            >
              <img
                src={listViewIcon}
                alt="View Permissions"
              />
            </button>
          </Tooltip>
          <Tooltip title={viewRestrictedAccess ? 'Show All Permissions' : 'Show Restricted'} arrow>
            <button
              onClick={() => {
                setViewRestrictedAccess((prev) => !prev);
                setViewOnlyAccess(false);
              }}
              className={`expand-permission-icon-btn ${viewRestrictedAccess ? 'active-icon-btn' : ''} ${!roleValue && !employeeValue ? 'disabled-btn' : ''}`}
              aria-pressed={viewRestrictedAccess}
              disabled={(!roleValue && !employeeValue) || isLoading || Boolean(employeeError)}
              type="button"
            >
              <img
                src={rejectPurpleIcon}
                alt="Show Restricted"
              />
            </button>
          </Tooltip>
          <Tooltip title={isAllExpanded ? 'Collapse All' : 'Expand All'} arrow>
            <button
              onClick={handleToggleExpandAll}
              className="expand-permission-icon-btn"
              type="button"
            >
              <img
                src={isAllExpanded ? collapseIcon : expandIcon}
                alt={isAllExpanded ? 'Collapse All' : 'Expand All'}
              />
            </button>
          </Tooltip>
          <Tooltip title={'Download'} arrow>
            <button
              onClick={handleDownloadClick}
              disabled={isLoading || Boolean(employeeError)}
              className="expand-permission-icon-btn"
              type="button"
            >
              <img
                src={downloadIcon}
                alt="Download"
              />
            </button>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
          >
            <MenuItem
              onClick={handleDownloadExcel}
              className="download-permission-dropdown"
              onMouseEnter={() => setIsExcelHovered(true)}
              onMouseLeave={() => setIsExcelHovered(false)}
            >
              <img
                src={isExcelHovered ? excelhoverIcon : excelIcon}
                style={{ marginRight: 8 }}
                alt="excel Icon"
              />{' '}
              Download as Excel
            </MenuItem>
          </Menu>
        </div>
      </div>
      {directoryError && <div className="permission-employee-summary" role="alert">{directoryError}</div>}
      {employeeValue && !isLoading && employeeError && (
        <div className="permission-employee-summary" role="alert">
          {employeeError}
        </div>
      )}
      {isLoading ? (
        <div className="permission-loader-container" role="status" aria-live="polite">
          <Loader text={employeeValue ? 'Loading employee permissions...' : ''} />
        </div>
      ) : employeeValue && !employeeError && employeeRoles.length === 0 ? (
        <div className="permission-employee-summary" role="status">
          No roles have been assigned to this employee.
        </div>
      ) : (
        !employeeError && (!employeeValue || employeeRoles.length > 0) && renderPermissionContent(config)
      )}
      {!employeeValue && (
        <div className="save-button-container">
          <ButtonComponent
            btnCss={'set-save-btn'}
            label={SAVE}
            variant="contained"
            onClick={() => {
              handleSaveOnClick();
            }}
          />
        </div>
      )}
      <SimpleSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        alertCss={`snackbar-provider ${snackbarSeverity}`}
      />
      <Dialog
        open={roleModalOpen}
        onClose={() => setRoleModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: 'role-access-dialog-paper',
        }}
      >
        <DialogTitle className="role-dialog-title">
          <span>{roleModalData?.title}</span>
          <IconButton onClick={() => setRoleModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="role-dialog-content">
          {!roleModalData?.roles?.length ? (
            <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: '16px 0' }}>
              No roles currently have access to this page/section.
            </Typography>
          ) : (
            <>
              <div className="role-action-filter-bar">
                <span className="filter-label">Filter Permissions:</span>
                {['View', 'Add', 'Edit', 'Delete'].map((actKey) => (
                  <FormControlLabel
                    key={actKey}
                    control={
                      <Checkbox
                        size="small"
                        checked={Boolean(actionFilters[actKey])}
                        onChange={(e) =>
                          setActionFilters((prev) => ({
                            ...prev,
                            [actKey]: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={actKey}
                    className="filter-checkbox-label"
                  />
                ))}
              </div>
              <div className="role-access-table-container">
                <table className="role-access-table">
                  <thead>
                    <tr>
                      <th>Role</th>
                      {actionFilters.View && <th>View</th>}
                      {actionFilters.Add && <th>Add</th>}
                      {actionFilters.Edit && <th>Edit</th>}
                      {actionFilters.Delete && <th>Delete</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {roleModalData.roles
                      .filter((item) => {
                        const lowerActions = (item.actions || []).map((a) => String(a).toLowerCase());
                        const hasView = lowerActions.includes('read') || lowerActions.includes('view');
                        const hasAdd = lowerActions.includes('create') || lowerActions.includes('add');
                        const hasEdit = lowerActions.includes('update') || lowerActions.includes('edit');
                        const hasDelete = lowerActions.includes('delete');

                        // If no checkboxes selected, show all
                        const anyFilterActive = Object.values(actionFilters).some(Boolean);
                        if (!anyFilterActive) return true;

                        // Only show role if it has 'Y' for at least one selected filter
                        return (
                          (actionFilters.View && hasView) ||
                          (actionFilters.Add && hasAdd) ||
                          (actionFilters.Edit && hasEdit) ||
                          (actionFilters.Delete && hasDelete)
                        );
                      })
                      .map((item) => {
                        const lowerActions = (item.actions || []).map((a) => String(a).toLowerCase());
                        const hasView = lowerActions.includes('read') || lowerActions.includes('view');
                        const hasAdd = lowerActions.includes('create') || lowerActions.includes('add');
                        const hasEdit = lowerActions.includes('update') || lowerActions.includes('edit');
                        const hasDelete = lowerActions.includes('delete');

                        return (
                          <tr key={item.roleId}>
                            <td className="role-name-cell">{item.roleName}</td>
                            {actionFilters.View && (
                              <td className={`perm-cell ${hasView ? 'yes' : 'no'}`}>
                                {hasView ? 'Y' : 'N'}
                              </td>
                            )}
                            {actionFilters.Add && (
                              <td className={`perm-cell ${hasAdd ? 'yes' : 'no'}`}>
                                {hasAdd ? 'Y' : 'N'}
                              </td>
                            )}
                            {actionFilters.Edit && (
                              <td className={`perm-cell ${hasEdit ? 'yes' : 'no'}`}>
                                {hasEdit ? 'Y' : 'N'}
                              </td>
                            )}
                            {actionFilters.Delete && (
                              <td className={`perm-cell ${hasDelete ? 'yes' : 'no'}`}>
                                {hasDelete ? 'Y' : 'N'}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions className="role-dialog-actions">
          <ButtonComponent
            label="Close"
            variant="contained"
            onClick={() => setRoleModalOpen(false)}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Permission;

